import { makeAutoObservable } from 'mobx'
import { chunk } from 'lodash'
import { type CancelToken } from 'axios'
import { type AxiosOptions, errorHandler } from 'shared/api'
import { type IIconsVector, showToast } from 'shared/ui'
import { logger, truthy } from 'shared/lib'
import { ContactsApi } from 'entities/Contacts/api/contacts'
import { Contact } from 'entities/Contacts/model/Contact'
import { type Tag } from 'entities/Tags/model/Tag'
import type {
  IParamsContactsSearch,
  IParamsCreateContact,
  IResponseContact,
  IResponseContactAll,
  IResponseContactCreateError,
} from 'entities/Contacts/api/types'
import { contactTemplate } from 'entities/Contacts/templates/contactTemplate'
import {
  type CreateContactErrors,
  TrialContactsLimitModal,
} from 'entities/Subscription/ui/TrialContactsLimitModal'
import { ContactChoosePhoneModalStore } from 'entities/Contacts/ui/ContactChoosePhoneModal'
import { type ConversationsStore } from 'entities/Conversation'
import type { SubscriptionStore } from 'entities/Subscription'
import type { UsersStore } from 'entities/Users'
import {
  type IIntegrationKey,
  IntegrationKey,
  IntegrationsApi,
  type IntegrationsStore,
} from 'entities/Integrations'
import { ContactActivecampaign } from 'entities/Contacts/model/Integrations/ContactActivecampaign'
import type { IContactAll } from 'entities/Contacts'
import { ContactHubspot } from 'entities/Contacts/model/Integrations/ContactHubspot'
import { ContactInfusionsoft } from 'entities/Contacts/model/Integrations/ContactInfusionsoft'
import { ContactPipedrive } from 'entities/Contacts/model/Integrations/ContactPipedrive'
import { ContactSalesforce } from 'entities/Contacts/model/Integrations/ContactSalesforce'
import { ContactUnified } from 'entities/Contacts/model/Integrations/ContactUnified'

export type BlockContactModalCallbacks = {
  resetActiveAndCloseDetailsIsMobile?: () => void
}

export class ContactsStore {
  itemsMap: Map<number, Contact> = new Map()
  private readonly _contactChoosePhoneModalStore: ContactChoosePhoneModalStore
  private _blockContactModalCallbacks: BlockContactModalCallbacks | null = null

  constructor(
    private _conversationStore: ConversationsStore,
    private _subscriptionStore: SubscriptionStore,
    private _usersStore: UsersStore,
    private _integrationsStore: IntegrationsStore
  ) {
    this._contactChoosePhoneModalStore = new ContactChoosePhoneModalStore(
      this,
      this._integrationsStore
    )

    makeAutoObservable(this)
  }

  get contactChoosePhoneModalStore() {
    return this._contactChoosePhoneModalStore
  }

  setBlockContactModalCallbacks = (callbacks: BlockContactModalCallbacks | null) => {
    this._blockContactModalCallbacks = callbacks
  }

  getBlockContactModalCallbacks(): BlockContactModalCallbacks {
    return this._blockContactModalCallbacks ?? {}
  }

  reset = (exclude?: number[]) => {
    const itemsKeep = Array.isArray(exclude)
      ? Array.from(this.itemsMap.values()).filter((item) => exclude.includes(item.id))
      : []

    this.itemsMap.clear()

    itemsKeep.forEach((item) => {
      this.itemsMap.set(item.id, item)
    })

    this._blockContactModalCallbacks = null
  }

  addItemTags = async (id: number, tags: Tag[]) => {
    const contact = await this.getItem(id)
    if (!contact) return

    const tagsMap = new Map<number, Tag>()
    contact.tags.forEach((item) => {
      tagsMap.set(item.id, item)
    })
    tags.forEach((item) => {
      tagsMap.set(item.id, item)
    })

    contact.tags = Array.from(tagsMap.values())

    return this.itemsMap.set(id, contact)
  }

  removeItemTags = async (id: number, tagsIds: string[]) => {
    const contact = await this.getItem(id)
    if (!contact) return

    const tagsMap = new Map<number, Tag>()
    contact.tags.forEach((item) => {
      tagsMap.set(item.id, item)
    })

    tagsIds.forEach((tagId) => {
      tagsMap.delete(Number(tagId))
    })

    contact.tags = Array.from(tagsMap.values())

    return this.itemsMap.set(id, contact)
  }

  addItems = (items: IResponseContact[]) => {
    items.forEach((item) => {
      this.addItem(item)
    })

    return items.reduce<Contact[]>((state, item) => {
      const contact = this.getItem(item.id)

      return contact ? [...state, contact] : [...state]
    }, [])
  }

  addItem = (item: IResponseContact) => {
    if (!item) return

    if (item.conversation) {
      this._conversationStore.updateItem(item.conversation)
    }

    this.itemsMap.set(item.id, new Contact(item))

    return this.getItem(item.id)
  }

  createEmptyContact = (
    contact?: Partial<IResponseContact>
  ): IResponseContact & { icon?: IIconsVector } => ({
    ...contactTemplate,
    id: 0,
    first_name: 'Name',
    last_name: '',
    formatted_number: 'Phone number',
    icon: 'personFilled',
    ...contact,
  })

  updateItem = (data: IResponseContact) => {
    const contact = this.getItem(data.id)

    if (contact) {
      contact.syncOrigin({
        ...contact.origin,
        ...data,
      })
    } else {
      this.addItem(data)
    }
  }

  hasItem = (id: number) => {
    return this.itemsMap.has(id)
  }

  getItem = (id?: number) => {
    if (typeof id !== 'number') return

    return this.itemsMap.get(id)
  }

  getById = async (id: number, update?: boolean, options?: AxiosOptions) => {
    let contact = this.getItem(id)

    if (!contact) {
      const { data } = await ContactsApi.getContactById(id, options)

      contact = new Contact(data)

      if (update) {
        contact = this.addItem(data)
      }
    }

    return contact
  }

  getByIds = async (ids: number[]): Promise<Contact[]> => {
    try {
      const res = await Promise.all(chunk(ids, 100).map(ContactsApi.getContactsByIds))
      const contacts: Contact[] = []
      res.forEach((item) => {
        item.data.data.forEach((item) => {
          contacts.push(new Contact(item))
        })
      })
      return contacts
    } catch (e) {
      logger.error(e)

      return []
    }
  }

  blockContactById = async (id: number) => {
    const { data } = await ContactsApi.blockContactById(id)

    this.updateItem(data)

    return data
  }

  unblockContactById = async (id: number) => {
    const { data } = await ContactsApi.unblockContactById(id)

    this.updateItem(data)
  }

  getContactByNumber = async (number: string, withCreate?: boolean) => {
    try {
      const {
        data: { data },
      } = await ContactsApi.getContacts({
        page: 1,
        search: number,
      })

      if (data.length === 1) {
        return new Contact(data[0])
      }

      if (withCreate) {
        return await this.createContact({
          number,
        })
      }

      return
    } catch (e) {
      logger.error(e)

      return
    }
  }

  createContact = async (
    params: IParamsCreateContact,
    errorConfig?: {
      catchError: CreateContactErrors
      onError?: () => void
    }
  ) => {
    try {
      const { data: contact } = await ContactsApi.createContact({ ...params, forLink: true })

      this.addItem(contact)

      return new Contact(contact)
    } catch (e) {
      let contact: Contact | undefined = undefined
      const err = e as Error
      const { type, error } = await errorHandler<IResponseContactCreateError>(err)

      if (type === 'axios-error') {
        const data = error.response?.data
        const contactId = data?.contact_id

        if (contactId) {
          contact = await this.getById(contactId)
        } else {
          const error = (data?.email && data?.email[0]) || (data?.number && data?.number[0])

          if (errorConfig && errorConfig.catchError === error) {
            errorConfig.onError?.()

            new TrialContactsLimitModal(this._subscriptionStore, this._usersStore).open(error)
          } else if (error) {
            showToast({
              title: error,
              type: 'error',
            })
          }
        }
      }

      return contact
    }
  }

  createContactByResponse = (item: IResponseContactAll) => {
    if ('integration_type' in item) {
      if (item.integration_type === 'hubspot') {
        return new ContactHubspot(item)
      }

      if (item.integration_type === 'activecampaign') {
        return new ContactActivecampaign(item)
      }

      if (item.integration_type === 'infusionsoft') {
        return new ContactInfusionsoft(item)
      }

      if (item.integration_type === 'pipedrive') {
        return new ContactPipedrive(item)
      }

      if (item.integration_type === 'salesforce') {
        return new ContactSalesforce(item)
      }

      const integration = this._integrationsStore.getIntegration(item.integration_type)
      if (integration?.is_ampersand || integration?.isNangoProvider) {
        return new ContactUnified(item)
      }
    } else {
      this.updateItem(item)
      return new Contact(item)
    }
  }

  getIntegrationSearchContact = async (
    key: IIntegrationKey,
    params: IParamsContactsSearch,
    cancelToken?: CancelToken
  ): Promise<
    | {
        total: number
        contacts: IContactAll[]
      }
    | undefined
  > => {
    if (key === IntegrationKey.salesmsg)
      return ContactsApi.getContactsSearch(params, {
        cancelToken,
      }).then(({ data }) => ({
        total: data.total,
        contacts: data.results.map(this.createContactByResponse).filter(truthy),
      }))
    if (key === 'activecampaign') {
      return IntegrationsApi.getIntegrationsContactsActivecampaign(params, {
        cancelToken,
      }).then(({ data }) => ({
        total: data.total,
        contacts: data.results.map(this.createContactByResponse).filter(truthy),
      }))
    }
    if (key === 'hubspot') {
      return IntegrationsApi.getIntegrationsContactsHubspot(params, {
        cancelToken,
      }).then(({ data }) => ({
        total: data.total,
        contacts: data.results.map(this.createContactByResponse).filter(truthy),
      }))
    }
    if (key === 'infusionsoft') {
      return IntegrationsApi.getIntegrationsContactsInfusionsoft(params, {
        cancelToken,
      }).then(({ data }) => ({
        total: data.total,
        contacts: data.results.map(this.createContactByResponse).filter(truthy),
      }))
    }
    if (key === 'pipedrive') {
      return IntegrationsApi.getIntegrationsContactsPipedrive(params, {
        cancelToken,
      }).then(({ data }) => ({
        total: data.total,
        contacts: data.results.map(this.createContactByResponse).filter(truthy),
      }))
    }
    if (key === 'salesforce') {
      return IntegrationsApi.getIntegrationsContactsSalesforce(params, {
        cancelToken,
      }).then(({ data }) => ({
        total: data.total,
        contacts: data.results.map(this.createContactByResponse).filter(truthy),
      }))
    }

    const integration = this._integrationsStore.getIntegration(key)

    if (integration?.isContactIntegration) {
      return IntegrationsApi.getIntegrationsUnifiedContacts(key, params, {
        cancelToken,
      }).then(({ data }) => ({
        total: data.total,
        contacts: data.results.map(this.createContactByResponse).filter(truthy),
      }))
    }
  }
}
